<template>
	
	<span>
		
		<Menu
			v-if="typeof $auth != 'undefined' && $auth !== null && !$auth.loading && $auth.isAuthenticated"
		/>

		<Menu4
			v-else/>

		<Loading
			v-if="waiting"
			:isLoading="waiting"/>
		
		<CreateTaskTemplatePopup
			:taskTemplate="selectedTemplate"
			@add:template="saveTemplate"
			@addTask:template="addTaskToTemplate"
			@addChild:template="addChildTaskToTemplate"
			@cancel:template="cancelTemplate"
			@delete:template="deleteTemplate"
			@removeTask:template="removeTaskFromTemplate"/>

		<DisplayTaskTemplatePopup
			:taskTemplate="selectedTemplate"
			@import:template="importTemplate"
			@instantiate:template="prepareTemplateForInstantiation"
			@delete:template="deleteTemplate"
			@publish:template="publishTemplate"/>

		<InstantiateTaskTemplatePopup
			:taskTemplate="selectedTemplate"
			:tasks="tasksFromTemplate"
			@cancelInstantiation:template="cancelInstantiation"
			@instantiate:template="instantiateTemplate"/>

		<AppLayout
			v-if="!waiting"
			:title="localSelectedGallery.text + ' Gallery'"
			:subtitle="!$auth.loading && !$auth.isAuthenticated ? 'Task templates are great way to document the set of things you need to complete when you need to get something done. This gallery shows off the templates shared by Principal Task users.' : ''"
			:showDefaultControls="false"
		>
			<!-- Main Content Slot -->
			<template #content>
			<div class="gallery-content">
			<!-- Create New Button Row -->
			<b-row 
				v-if="localGalleries.personal.active"
				class="mb-4">
				<b-col cols="12" class="text-end">
					<!-- 20260423 -- Copilot (Claude Sonnet 4.6) -- Fixed isPremiumUser missing () - was evaluating function ref (always truthy) instead of calling it -->
					<b-button
						v-if="isPremiumUser()"
						id="create-template-button"
						@click="prepareTemplateForCreation"
						variant="success"
						class="d-inline-flex align-items-center">
						<b-icon icon="plus-circle" class="me-2" />
						&nbsp; Create New Template
					</b-button>

					<b-tooltip
						v-if="isPremiumUser()"
						target="create-template-button"
						triggers="hover"
						variant="success">
						Click to create a new template.
					</b-tooltip>

					<b-button
						v-if="!isPremiumUser()"
						id="create-template-button"
						class="d-inline-flex align-items-center">
						<b-icon icon="plus-circle" class="me-2" />
						Create New Template
					</b-button>

					<PremiumTooltip
						v-if="!isPremiumUser()"
						target="create-template-button"
						triggers="hover"
						variant="warning"
						placement="topleft"/>
				</b-col>
			</b-row>

			<!-- Empty State -->
			<b-row 
				v-if="typeof localTemplates == 'undefined' || localTemplates === null || localTemplates.length == 0"
				class="empty-state">
				<b-col cols="12">
					<div class="text-center py-5">
						<b-icon-clipboard style="font-size: 4rem; color: #6c757d;" class="mb-3" />
						<h3>No templates to display</h3>
						<p class="text-muted">
							{{ localGalleries.personal.active ? 'Create your first template to get started.' : 'Check back later for new templates.' }}
						</p>
					</div>
				</b-col>
			</b-row>
			
			<!-- Template Cards Grid -->
			<b-row 
				v-else
				class="g-4">
				<b-col 
					v-for="(template, templateIndex) in localTemplates"
					:key="templateIndex"
					cols="12" 
					md="6" 
					lg="4"
					class="mb-4">
					<b-card 
						class="template-card h-100"
						:bg-variant="template.bgVariant">

						<template v-slot:header>
							<div class="card-header-content d-flex justify-content-between align-items-center">
								<div class="flex-grow-1">
									<div
										@click="displayTemplateDetails(template)"
										:id="'template-name-link_' + templateIndex"
										class="template-title"
										style="color: #ffffff; font-size: 1.5rem; font-weight: 600; cursor: pointer;">
										{{template.name}}
									</div>
									<b-tooltip 
										:target="'template-name-link_' + templateIndex"
										triggers="hover"
										variant="primary">
										Click to view details.
									</b-tooltip>
								</div>
								
								<div class="d-flex align-items-center gap-2">
									<b-icon 
										v-if="localSelectedGallery.value == 'personal'"
										:id="'template-edit-icon_' + templateIndex"
										icon="pencil-square"
										font-scale="1.3"
										class="cursor-pointer"
										style="color: #ffffff;"
										@click="editTemplate(template)"></b-icon>
									
									<b-tooltip 
										v-if="localSelectedGallery.value == 'personal'"
										:target="'template-edit-icon_' + templateIndex"
										triggers="hover"
										variant="primary">
										Click to edit this template.
									</b-tooltip>

									<b-badge 
										v-if="localSelectedGallery.value == 'public'"
										:id="'template-timeUsed_' + templateIndex"
										variant="light"
										pill>
										<b-icon icon="star-fill" font-scale="0.8" class="me-1" style="color: #ffc107;" />
										{{template.timesUsed}}
									</b-badge>

									<b-tooltip 
										v-if="localSelectedGallery.value == 'public'"
										:target="'template-timeUsed_' + templateIndex"
										triggers="hover"
										variant="primary">
										Used {{template.timesUsed}} time(s).
									</b-tooltip>
								</div>
							</div>
						</template>
						
						<b-card-body class="template-description">
							<vue-markdown :source="template.description" />
						</b-card-body>

						<template v-slot:footer>
							<div class="d-flex justify-content-between align-items-center text-muted">
								<small class="d-flex align-items-center">
									<b-icon icon="person-circle" font-scale="1.1" style="margin-right: 0.75rem;" />
									<span v-if="typeof template.importedFrom != 'undefined' && template.importedFrom !== null">
										Imported
									</span>
									<span v-else>
										{{template.ownerUser[0] != null ? template.ownerUser[0].goesBy || template.ownerUser[0].firstName : ""}}
									</span>
								</small>
								<small class="d-flex align-items-center">
									<b-icon icon="calendar3" font-scale="1.1" style="margin-right: 0.75rem;" />
									{{getDateString(template.added)}}
								</small>
							</div>
						</template>

					</b-card>
				</b-col>
			</b-row>
			</div>
			</template>
		</AppLayout>
	</span>

</template>

<script>

import _ from 'lodash';
import AppLayout from '@/components/AppLayout.vue'
import CreateTaskTemplatePopup from '@/components/popups/CreateTaskTemplate.vue'
import DisplayTaskTemplatePopup from '@/components/popups/DisplayTaskTemplate.vue'
import genericOps from '@/mixins/genericOps.js'
import InstantiateTaskTemplatePopup from '@/components/popups/InstantiateTaskTemplate.vue'
import Loading from '@/components/Loading.vue'
import Menu from '@/components/menus/UserMenu.vue'
import Menu4 from '@/components/menus/Menu4.vue'
import PremiumTooltip from '@/components/PremiumTooltip.vue'
import TaskService from '@/services/TaskService'
import TaskTemplateService from '@/services/TaskTemplateService'
import VueMarkdown from 'vue-markdown'

export default {
	name: 'Gallery',
	metaInfo() {
        return {
            title: 'Principal Task | Gallery',
            meta: [
				{
					name: 'description',
					content: 'The Principal Task Gallery showcases the todo templates shared by its users.'
                },
                {
                    property: 'og:description',
                    content: 'The Principal Task Gallery showcases the todo templates shared by its users.'
                },
                {
                    property: 'og:image',
                    content: 'http://www.principaltask.com/img/PTLogo-Cropped-1.fafd2068.png'
                },
                {
                    property: 'og:title',
                    content: 'Principal Task | Gallery'
                },
                {
                    property: 'og:type',
                    content: 'website'
                },
                {
                    name: 'twitter:card',
                    content: 'summary_large_image'
                },
                {
                    name: 'twitter:description',
                    content: 'The Principal Task Gallery showcases the todo templates shared by its users.'
                },
                {
                    property: 'twitter:image',
                    content: 'https://www.principaltask.com/img/PTLogo-Cropped-1.fafd2068.png'
                },
                {
                    name: 'twitter:site',
                    content: '@PrincipalTask'
                },
                {
                    name: 'twitter:title',
                    content: 'Principal Task | Gallery'
                },
			]
        }
    },
    	components: {
		AppLayout,
		CreateTaskTemplatePopup,
		DisplayTaskTemplatePopup,
		InstantiateTaskTemplatePopup,
		Loading,
		Menu,
		Menu4,
		PremiumTooltip,
		'vue-markdown': VueMarkdown,
	},
	mixins: [genericOps],
	data() {
		return {
			categories: [
				{
					text: 'Birthdays',
					value: 'birthdays'
				},
				{
					text: 'Anniversaries',
					value: 'anniversaries'
				},
			],
			emptyTemplate: {
				name: null, 
				description: null, 
				public: false, 
				tasks: [{
					title:null, 
					children: []
				}]
			},
			galleries: {
				corporate: {
					text: 'Corporate',
					value: 'corporate',
					active: false,
					to: {name: 'gallery', params: {gallery: 'corporate'}}
				},
				personal: {
					text: 'Personal',
					value: 'personal',
					active: false,
					to: {name: 'gallery', params: {gallery: 'personal'}}
				},
				public: {
					text: 'Public',
					value: 'public',
					active: false,
					to: {name: 'gallery-public'}
				}
			},
			searchString: null,
			selectedCategory: 'all',
			selectedGallery: {
				text: 'Personal',
				value: 'personal'
			},
			selectedTemplate: {
				name: null,
				public: false,
				tasks: [{children:[]}]
			},
			tasksFromTemplate: [],
			templates: [
				{
					name: 'Anniversary, General',
					description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Morbi sagittis bibendum nisl, at luctus dolor consectetur at. Etiam vitae ligula vel orci iaculis semper ac eu nunc. Nunc sollicitudin pulvinar arcu vitae vestibulum. Ut tempus nisi nisl justo.',
					icon: '',
					image: '',
					category: '',
					uses: 0,
					'bgVariant': 'info',
					'textVariant': 'dark',
					tasks: [
						{
							title: 'Parent Task #1',
							children: [
								{title: 'Parent Task #1, Child #1'},
								{title: 'Parent Task #1, Child #2'},
								{title: 'Parent Task #1, Child #3'},
							]
						},
						{
							title: 'Parent Task #2',
							children: [
								{title: 'Parent Task #2, Child #1'},								
							]
						},
						{
							title: 'Parent Task #3',
							children: [
								{title: 'Parent Task #3, Child #1'},								
							]
						},
						{
							title: 'Parent Task #4',
							children: [
								{title: 'Parent Task #4, Child #1'},
								{title: 'Parent Task #4, Child #2'},
								{title: 'Parent Task #4, Child #3'},
								{title: 'Parent Task #4, Child #4'},
								{title: 'Parent Task #4, Child #5'},								
							]
						}
					]
				},
				{
					name: 'Anniversary, Fancy',
					description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Fusce tincidunt gravida nunc id tempus. Sed lacinia nisi in arcu laoreet egestas. Integer vel bibendum odio. Donec a purus quam. Nunc id est eget quam facilisis fermentum. Suspendisse id neque ac metus accumsan rhoncus. Praesent eu magna id diam scelerisque pharetra sed hendrerit mi. Suspendisse eu est ac est maximus iaculis nec quis odio.  Aliquam consequat orci magna, eleifend laoreet nibh maximus vitae. Donec semper interdum augue libero.',
					icon: '',
					image: '',
					category: '',
					uses: 0,
					'bgVariant': 'success',
					'textVariant': 'dark',
					tasks: [
						{
							title: 'Parent Task #1',
							children: [
								{title: 'Parent Task #1, Child #1'},
							]
						},
					]
				},
				{
					name: 'On-boarding a new Client',
					description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Cras vitae rutrum quam, id malesuada est. Ut tortor mi, luctus sed varius id, venenatis vitae tortor. Nunc condimentum at felis in rutrum. Praesent feugiat gravida quam, vitae imperdiet velit tincidunt vel. Etiam in mattis enim, vel imperdiet nisl. Quisque scelerisque, ipsum sed mollis vehicula, urna magna varius turpis, id vehicula leo dolor quis diam. Curabitur eget nunc pellentesque, luctus ligula eget, molestie magna. Nulla erat curae.',
					icon: 'person-plus',
					image: '',
					category: '',
					uses: 0,
					'bgVariant': 'danger',
					'textVariant': 'light',
					tasks: [
						{
							title: 'Parent Task #1',
							children: [
								{title: 'Parent Task #1, Child #1'},
								{title: 'Parent Task #1, Child #2'},
							]
						},
					]
				},
				{
					name: 'Preparing for a vacation',
					description: 'lorem ipsum',
					icon: 'map',
					image: '',
					category: '',
					uses: 0,
					'bgVariant': 'primary',
					'textVariant': 'dark',
					tasks: [
						{
							title: 'Parent Task #1',
							children: [
								{title: 'Parent Task #1, Child #1'},
								{title: 'Parent Task #1, Child #2'},
								{title: 'Parent Task #1, Child #3'},
							]
						},
					]
				},
				{
					name: 'Preparing for a business trip',
					description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Fusce tincidunt gravida nunc id tempus. Sed lacinia nisi in arcu laoreet egestas. Integer vel bibendum odio. Donec a purus quam. Nunc id est eget quam facilisis fermentum. Suspendisse id neque ac metus accumsan rhoncus. Praesent eu magna id diam scelerisque pharetra sed hendrerit mi. Suspendisse eu est ac est maximus iaculis nec quis odio.  Aliquam consequat orci magna, eleifend laoreet nibh maximus vitae. Donec semper interdum augue libero.',
					icon: 'briefcase',
					image: '',
					category: '',
					uses: 0,
					'bgVariant': 'warning',
					'textVariant': 'dark',
					tasks: [
						{
							title: 'Parent Task #1',
							children: [
								{title: 'Parent Task #1, Child #1'},
								{title: 'Parent Task #1, Child #2'},
								{title: 'Parent Task #1, Child #3'},
							]
						},
					]
				},
			],
			variants: {
				info: {
					body: 'info',
					text: 'dark'
				},
				primary: {
					body: 'primary',
					text: 'light'
				},
				success: {
					body: 'success',
					text: 'dark'
				},
				warning: {
					body: 'warning',
					text: 'dark'
				}
			},
			waiting: false
		}
	},
	computed: {
		localCategories: {
			get: function() {
				return this.categories
			},
			set: function(newCategories) {
				this.categories = newCategories
			}
		},
		localGalleries: {
			get: function() {
				return this.galleries
			},
			set: function(newGalleries) {
				this.galleries = newGalleries
			}
		},
		localSearchString: {
			get: function() {
				return this.searchString
			},
			set: function(newSearchString) {
				this.searchString = newSearchString
			}
		},
		localSelectedCategory: {
			get: function() {
				return this.selectedCategory
			},
			set: function(newSelectedCategory) {
				this.selectedCategory = newSelectedCategory
			}
		},
		localSelectedGallery: {
			get: function() {
				return this.selectedGallery
			},
			set: function(newSelectedGallery) {
				this.selectedGallery = newSelectedGallery
			}
		},
		localTemplates: {
			get: function() {
				return this.$store.getters.getTaskTemplates
			},
			set: function(newTemplates) {
				console.log('Something is trying to modify the templates array directly.')
			}
		},
		
	},
	watch: {
		$route: 'preparePage',
	},
	mounted() {
		this.preparePage()
	},
	methods: {
		addChildTaskToTemplate(template, index) {
			
			if(typeof this.selectedTemplate.tasks[index] != 'undefined' && this.selectedTemplate.tasks[index] !== null)
			{
				if(typeof this.selectedTemplate.tasks[index].children == 'undefined' || this.selectedTemplate.tasks[index].children == 'undefined' === null) {
					this.selectedTemplate.tasks[index].children = [{}]
				} else {
					this.selectedTemplate.tasks[index].children.push({})
				}
			} 
			// else {
			// 	console.log(this.selectedTemplate.tasks)
			// }
		},
		addTaskToTemplate(template, index, parentIndex, childIndex) {

			// 20201004 -- ZWS -- Adds a task (parent or child) to the template.
			// index: element's index in the array used to display the tasks.  Only valid for referencing parents.
			// parentIndex: the index of the parent element in the template's task array.  (Expected to be set by the process that create's the display array.)
			// childIndex: the index of "this" element in the task's children array.  (Expected to be set by the process that create's the display array.)

			// console.log('addTaskToTemplate: index = ' + index + ', ' + 'parentIndex = ' + parentIndex + ', childIndex = ' + childIndex)
			
			if(isNaN(parentIndex) && isNaN(childIndex)) {

				try {
					this.selectedTemplate.tasks.splice(index, 0, {children:[]})

					// Splice inserts an element before the index, rearrange to the
					// element appears after the index.
					let newElement = this.selectedTemplate.tasks[index]
					let previousElement = this.selectedTemplate.tasks[index + 1]
					this.selectedTemplate.tasks[index + 1] = newElement
					this.selectedTemplate.tasks[index] = previousElement
				} catch(e) {
					console.log('Error in Gallery.vue: ' + e)
					console.log('index = ' + index + ', ' + 'parentIndex = ' + parentIndex + ', childIndex = ' + childIndex)
				}

			} else {

				try {
					this.selectedTemplate.tasks[parentIndex].children.splice(childIndex, 0, {})

					// Splice inserts an element before the index, rearrange to the
					// element appears after the index.
					let newElement = this.selectedTemplate.tasks[parentIndex].children[childIndex]
					let previousElement = this.selectedTemplate.tasks[parentIndex].children[childIndex + 1]
					this.selectedTemplate.tasks[parentIndex].children[childIndex + 1] = newElement
					this.selectedTemplate.tasks[parentIndex].children[childIndex] = previousElement
				} catch(e) {
					console.log('Error in Gallery.vue: ' + e)
					console.log('index = ' + index + ', ' + 'parentIndex = ' + parentIndex + ', childIndex = ' + childIndex)
				}
				

			}

			// console.log('done.')

		}, 
		async cancelInstantiation(template, tasks) {
			
			this.$bvModal.hide('instantiateTaskTemplateModal')
			
			this.waiting = true

			let count = 0
			let deletedCount = 0
			
			// console.log('... deleting ' + tasks.length + ' task(s)')

			for(let task of tasks) {
				
				await TaskService.deleteTask(task.id, this.$store.getters.getUserToken).then((response) => {
					deletedCount++
					// console.log(response)
					// console.log(count + ', ' + deletedCount)
				})

				count++

			}

			if(count == deletedCount) {
				
				this.makeToast(true
					, 'Template Instantiation Cancelled'
					, deletedCount + ' of ' + count + ' task(s) deleted.'
					, 'success')
			} else {
				
				this.makeToast(true
					, 'Template Instantiation Cancelled'
					, deletedCount + ' of ' + count + ' task(s) deleted.  Your list may contain unnecessary tasks.'
					, 'warning')

			}

			
			this.waiting = false
		},
		cancelTemplate() {
			// console.log('canceling')
			this.selectedTemplate = _.cloneDeep(this.emptyTemplate)
			// console.log(this.selectedTemplate)
			this.$bvModal.hide('createTaskTemplateModal')
		
		},
		deleteTemplate(templateToDelete) {
			
			this.waiting = true
			this.$bvModal.hide('createTaskTemplateModal')
			this.$bvModal.hide('taskTemplateModal')

			this.$store.dispatch('deleteTaskTemplate', templateToDelete._id)


			
			// 20201010 -- ZWS -- @TODO Remove demo code once page linked with the database
			// let indexToDelete = -1
			
			// this.localTemplates.forEach((template, index) => {
			// 	if(template.name == templateToDelete.name) {
			// 		indexToDelete = index
			// 	}
			// })

			// if(indexToDelete > 0) {
			// 	this.localTemplates.splice(indexToDelete, 1)
			// }
			

			// this.selectedTemplate.name = null
			// this.selectedTemplate.description = null
			// this.selectedTemplate.tasks = []

			this.waiting = false
		}, 
		displayTemplateDetails(template) {
			
			this.selectedTemplate = _.cloneDeep(template)
			this.$bvModal.show('taskTemplateModal')
		},
		editTemplate(template) {
			// console.log('Editing:')
			// console.log(template)
			this.selectedTemplate = _.cloneDeep(template)
			this.$bvModal.show('createTaskTemplateModal')
		},
		getDateString(dateString) {
		   
			return TaskService.getStringFromMillis(dateString, 'MM/DD/YYYY')
         	},
		importTemplate(template) {

			this.$bvModal.hide('taskTemplateModal')
			
			let temp = _.cloneDeep(template)

			temp.importedFrom = template._id
			temp.public = false
			delete temp._id
			delete temp.publishedFrom

			TaskTemplateService.setSystemValues(temp)

			TaskTemplateService.addTemplate(temp, this.$store.getters.getUserToken)
			.then((response) => {

				temp._id = response.data.response.data._id

				TaskTemplateService.updateTimesUsed(template._id, this.$store.getters.getUserToken)

				this.makeToast(true,
					'Template Imported',
					'Template imported successfully and is available for use.',
					'success')

			})
			.catch((e) => {
				console.log('store.addTaskTemplate - Exception encountered: ')
				console.log(e)
				throw e
			})

		}, 
		async instantiateTemplate(template, tasks) {
			
			this.waiting = true
			
			this.$bvModal.hide('instantiateTaskTemplateModal')

			let createdCount = 0
			let count = 0

			for (let task of tasks) {

				let temp = task

				TaskService.setSystemValues(temp)
				TaskService.calculateDynamicFields(temp)

				// Remove field(s) that the database shouldn't store
				delete temp.childIndex
				delete temp.children
				delete temp.index
				delete temp.parentIndex
				delete temp.variant

				await TaskService.updateTask(temp, this.$store.getters.getUserToken).then(async (response) => {

					createdCount++

				}).catch((e) => {
					console.log('EXCEPTION')
					console.log(e)
				})

				count++
				

			}

			this.makeToast(true
				, 'Template Instantiated'
				, createdCount + ' of ' + count + ' task(s) were created from the selected template.'
				, 'success')

			TaskTemplateService.updateTimesUsed(template._id, this.$store.getters.getUserToken)

			this.waiting = false

		},
		async preparePage() {
			
			this.waiting = true

			let templateFilter = {}
			
			// Reset Variables
			this.localGalleries.corporate.active = false
			this.localGalleries.personal.active = false
			this.localGalleries.public.active = false
			
			let routedGallery = this.$route.params.gallery
			let routedUser = this.$route.params.userId

			if(typeof routedGallery == 'undefined' || routedGallery === null || routedGallery == 'public') {
				this.localSelectedGallery = this.localGalleries.public
				this.localGalleries.public.active = true

				templateFilter.public = true

			} else if(routedGallery == 'corporate') {
				this.localSelectedGallery = this.localGalleries.corporate
				this.localGalleries.corporate.active = true
			} else if(routedGallery == 'personal') {
				
				this.localSelectedGallery = this.localGalleries.personal
				this.localGalleries.personal.active = true

				templateFilter.public = false

			}

			if(typeof routedUser != 'undefined' && routedUser === null) {
			
				templateFilter.owner = routedUser
			
			}

			await this.$store.dispatch('loadTaskTemplates', {filter:templateFilter})

			// console.log(routedGallery 
			// 	+ ', Public: ' + this.localGalleries.public.active 
			// 	+ ', Corporate: ' + this.localGalleries.corporate.active 
			// 	+ ', Personal: ' + this.localGalleries.personal.active)

			this.waiting = false

		},
		prepareTemplateForCreation() {

			this.selectedTemplate = _.cloneDeep(this.emptyTemplate)

			// Show the add/edit dialog.
			this.$bvModal.show('createTaskTemplateModal')
	

		},
		async prepareTemplateForInstantiation(template) {
			
			this.waiting = true
			this.$bvModal.hide('taskTemplateModal')

			// console.log('instantiating:')
			// console.log(template)

			let tasksCreated = true
			
			// 20201003 -- ZWS -- Flatten tasks and their children so that
			// existing components can be used to display the task data.  The
			// process by which these tasks are created will need to account
			// for the flattening.

			this.tasksFromTemplate = []

			let index = 0

			for (let task of template.tasks) {

				// console.log('Processing parent ' + task.title)

				TaskService.setSystemValues(task)
				TaskService.calculateDynamicFields(task)
				
				let temp = _.cloneDeep(task)

				// Remove field(s) that the database shouldn't store or that shouldn't be carried-over from the template
				delete temp.children
				delete temp.index
				delete temp._id

				await TaskService.addTask(temp, this.$store.getters.getUserToken).then(async (response) => {

					task._id = response.data.response.data[0]._id

					// console.log('Pushed ' + temp.title)
					this.tasksFromTemplate.push(task)

					for (let child of task.children) {
						
						// console.log('Processing child ' + child.title)

						TaskService.setSystemValues(child)
						TaskService.calculateDynamicFields(child)
						
						child.parent = task._id

						let temp2 = _.cloneDeep(child)
						delete temp2._id
						
						await TaskService.addTask(temp2, this.$store.getters.getUserToken).then((response) => {
							
							child._id = response.data.response.data[0]._id
						
							child.parentTask = [temp]
							
							child.parentIndex = index

							// console.log('Pushed ' + temp2.title)
							this.tasksFromTemplate.push(child)

							

						}).catch((e) => {
							tasksCreated = false
							console.log(e)
							console.log(JSON.stringify(e))
							this.makeToast(true, 'Task Template', 'Error preparing template for instantiation: ' + e.message, 'danger')

						})

						
					}

				}).catch((e) => {
					tasksCreated = false
					console.log(e)
					console.log(JSON.stringify(e))
					this.makeToast(true, 'Task Template', 'Error preparing template for instantiation: ' + e.message, 'danger')
				})

				index++
				

			}

			this.waiting = false
			
			if(tasksCreated) {
				this.$bvModal.show('instantiateTaskTemplateModal')
			}
			

			/*
			20201007 -- ZWS -- Logic for UI testing.
			template.tasks.forEach((task, index) => {
				
				let temp = task
				
				// @TODO -- 20201003 -- ZWS -- Kludge necessary to allow TaskTableRow to display correctly.
				temp._id = 'TEMP_' + index

				this.tasksFromTemplate.push(temp)

				task.children.forEach((child, childIndex) => {
					let temp2 = child
					temp2.parentIndex = index

					// @TODO -- 20201003 -- ZWS -- Kludge necessary to allow TaskTableRow to display correctly.
					temp2._id = 'TEMP_' + index + '_' + childIndex

					this.tasksFromTemplate.push(temp2)
				})

			})
			*/

			
		
		},
		publishTemplate(template) {

			this.$bvModal.hide('taskTemplateModal')
			
			let temp = _.cloneDeep(template)

			temp.publishedFrom = template._id
			temp.public = true
			delete temp._id
			delete temp.importedFrom

			TaskTemplateService.setSystemValues(temp)

			TaskTemplateService.addTemplate(temp, this.$store.getters.getUserToken)
			.then((response) => {

				temp._id = response.data.response.data._id

				this.makeToast(true,
					'Template Published',
					'Template published successfully and is available for others to use.',
					'success')

			})
			.catch((e) => {
				console.log('store.addTaskTemplate - Exception encountered: ')
				console.log(e)
				throw e
			})

		},
		removeTaskFromTemplate(template, index, parentIndex, childIndex) {

			// 20201004 -- ZWS -- Removes a task (parent or child) to the template.
			// index: element's index in the array used to display the tasks.  Only valid for referencing parents.
			// parentIndex: the index of the parent element in the template's task array.  (Expected to be set by the process that create's the display array.)
			// childIndex: the index of "this" element in the task's children array.  (Expected to be set by the process that create's the display array.)

			if(isNaN(parentIndex) && isNaN(childIndex)) {

				if(this.selectedTemplate.tasks.length > 1) {
					this.selectedTemplate.tasks.splice(index, 1)
				}
				

			} else {

				this.selectedTemplate.tasks[parentIndex].children.splice(childIndex, 1)

			}

		},
		async saveTemplate(template) {
			
			this.waiting = true
			this.$bvModal.hide('createTaskTemplateModal')
			
			TaskTemplateService.setSystemValues(template)

			await this.$store.dispatch('addTaskTemplate', {template: template}).then((response) => {

			
				this.selectedTemplate = _.cloneDeep(this.emptyTemplate)
				this.preparePage()

			})

			

			this.waiting = false
		},
	}
	    
}
</script>

<style scoped>
.gallery-content {
	padding: 0;
}

.template-card {
	transition: all 0.3s ease;
	border: 2px solid transparent;
	background: linear-gradient(to bottom, #ffffff 0%, #f8f9fa 100%);
	box-shadow: 0 2px 8px rgba(0,0,0,.1);
	border-radius: 8px;
	overflow: hidden;
}

.template-card:hover {
	transform: translateY(-6px);
	box-shadow: 0 8px 24px rgba(0,123,255,.2);
	border-color: #007bff;
}

.card-header-content {
	padding: 4px 0;
}

.template-card ::v-deep(.card-header) {
	background: #6c757d !important;
	border-bottom: none !important;
	color: white !important;
	padding: 1.25rem !important;
}

.template-card .card-header {
	background: #6c757d !important;
	border-bottom: none !important;
	color: white !important;
	padding: 1.25rem !important;
}

.template-title {
	color: white !important;
	cursor: pointer;
	user-select: none;
}

.template-title:hover {
	text-decoration: underline;
	opacity: 0.85;
}

.template-description {
	overflow: hidden;
	display: -webkit-box;
	-webkit-line-clamp: 4;
	-webkit-box-orient: vertical;
	min-height: 100px;
	color: #495057;
	line-height: 1.6;
}

.template-card .card-body {
	padding: 1.5rem;
}

.cursor-pointer {
	cursor: pointer;
	transition: opacity 0.2s ease;
}

.cursor-pointer:hover {
	opacity: 0.7;
}

.empty-state {
	min-height: 400px;
	display: flex;
	align-items: center;
	justify-content: center;
}

.template-card .card-footer {
	background: linear-gradient(to bottom, #f8f9fa 0%, #e9ecef 100%);
	border-top: 1px solid rgba(0,0,0,.1);
	padding: 1rem 1.25rem;
}

.template-card .badge {
	font-weight: 500;
	padding: 0.5rem 0.75rem;
	font-size: 0.875rem;
}
</style>